import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useSendMessageStore } from '@lib/store';
import { ButtonV2, Input, Textarea } from '@shared/widgets';
import CloseIcon from '@images/close.svg';

interface IForm {
  subject: string;
  body: string;
}
const DEFAULT_FORM: IForm = { subject: '', body: '' };

interface INewMessageProps {
  className?: string;
  isShown: boolean;
  onClose: () => void;
}

export const NewMessage = (props: INewMessageProps) => {
  const params = useParams();

  const { loading, success, sendMessage, clear } = useSendMessageStore();

  const [animation, setAnimation] = useState<boolean>(false);
  const [form, setForm] = useState<IForm>(DEFAULT_FORM);

  useEffect(() => {
    setAnimation(props.isShown);
  }, [props.isShown]);
  const transitionEnd = () => !props.isShown && setAnimation(false);

  useEffect(() => {
    if (loading || !success) return;
    clear();
    close();
  }, [loading, success]);

  const close = () => {
    setForm(DEFAULT_FORM);
    props.onClose();
  };
  const handleSubjectChange = (subject: string) => setForm((prev) => ({ ...prev, subject }));
  const handleBodyChange = (body: string) => setForm((prev) => ({ ...prev, body }));
  const send = () => sendMessage({ Body: form.body, Subject: form.subject }, params.dir === 'sent');

  return (
    <div
      className={clsx(
        'fixed bottom-[80px] left-1/2 z-[1500] ml-[177px] w-[450px] rounded-sm shadow-[12px_12px_24px_0_#00000080] max-xl:right-[20px] max-xl:left-auto max-xl:ml-0 max-md:top-0 max-md:right-0 max-md:bottom-0 max-md:left-0 max-md:h-full max-md:w-full',
        'new-message-modal',
        animation
          ? 'visible scale-100 opacity-100 transition-[transform_400ms_ease,opacity_300ms_ease,visibility_0s_linear_0s]'
          : 'invisible scale-95 opacity-0 transition-[transform_400ms_ease,opacity_300ms_ease,visibility_0s_linear_400ms]',
        !props.isShown && 'hidden',
        props.className,
      )}
      onTransitionEnd={transitionEnd}
    >
      <div className='absolute top-[-5px] left-0 flex h-[67px] w-full items-center rounded-t-sm bg-[#1f1f1f] pr-14 pl-6 font-medium text-[#929292] uppercase'>
        New Message
        <CloseIcon className='absolute top-1/2 right-[18px] -translate-y-1/2 cursor-pointer' onClick={close} />
      </div>
      <div className='rounded-sm bg-[#fff] pt-29.5 pb-18 max-md:h-full max-md:rounded-none'>
        <Input
          className='!absolute !top-[67px] !left-0 !h-[51px] !border-b !border-b-[#efefef] !px-6 !text-[#343434] placeholder:!text-[#626262] placeholder:!uppercase'
          placeholder='Title'
          value={form.subject}
          onChange={handleSubjectChange}
        />
        <Textarea
          className='!border-none !px-6 !py-4 !text-[#343434] placeholder:!text-[#626262] placeholder:!uppercase max-md:!h-full'
          placeholder='Enter new message'
          value={form.body}
          onChange={handleBodyChange}
        />
        <div className='absolute bottom-0 left-0 flex w-full justify-end p-4'>
          <ButtonV2 className='!h-[40px] px-5' disabled={!form.body || !form.subject || loading} onClick={send}>
            Send Message
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};
